import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import KVSelectWithPreview from './KVSelectWithPreview';

const KVReplacementModal = ({
  isOpen,
  onClose,
  selectedKV,
  keyVisuals = [],
  brands = [],
  categories = [],
  dependencyCheckResult,
  onReplacementConfirm
}) => {
  // State for replacement modes
  const [replacementMode, setReplacementMode] = useState('individual'); // 'bulk' or 'individual'
  const [bulkReplacementKV, setBulkReplacementKV] = useState(null);
  const [individualReplacements, setIndividualReplacements] = useState([]);
  
  // State for validation
  const [validationErrors, setValidationErrors] = useState([]);
  const [availableKVsByMedium, setAvailableKVsByMedium] = useState({});
  
  // Modal flow states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [replacementSummary, setReplacementSummary] = useState([]);

  // Mock branding items from dependency check - ENHANCED with proper Branding Medium filtering
  const mockBrandingItems = dependencyCheckResult?.dependentItems || [
    {
      id: 1,
      brandingId: 'BB-DHA-LIS-001',
      storeName: 'Dhaka Gulshan Store',
      storeCode: 'DHA',
      brandingMedium: 'Billboard',
      location: 'Gulshan-2, Dhaka',
      assignedTo: 'Store Manager',
      currentKVId: selectedKV?.kvId
    },
    {
      id: 2,
      brandingId: 'IS-CTG-CAV-002',
      storeName: 'Chittagong Agrabad Store',
      storeCode: 'CTG', 
      brandingMedium: 'In-Store Static',
      location: 'Agrabad, Chittagong',
      assignedTo: 'Regional Manager',
      currentKVId: selectedKV?.kvId
    },
    {
      id: 3,
      brandingId: 'WD-DHA-COC-003',
      storeName: 'Dhaka Dhanmondi Store',
      storeCode: 'DHA',
      brandingMedium: 'Window Display', 
      location: 'Dhanmondi-15, Dhaka',
      assignedTo: 'Store Manager',
      currentKVId: selectedKV?.kvId
    }
  ];

  useEffect(() => {
    if (isOpen && selectedKV) {
      // Reset all state when modal opens
      setReplacementMode('individual');
      setBulkReplacementKV(null);
      setIndividualReplacements([]);
      setValidationErrors([]);
      setSearchTerm('');
      setFilterBrand('');
      setFilterCategory('');
      setShowSummary(false);
      
      // Initialize individual replacements array
      const initialReplacements = mockBrandingItems?.map(item => ({
        ...item,
        replacementKVId: null,
        replacementKV: null
      }));
      setIndividualReplacements(initialReplacements);
      
      // Group available KVs by Branding Medium for filtering
      groupAvailableKVsByMedium();
    }
  }, [isOpen, selectedKV, keyVisuals]);

  // Group available KVs by Branding Medium for precise filtering
  const groupAvailableKVsByMedium = () => {
    const kvsByMedium = {};
    
    // Get unique branding mediums from dependencies
    const uniqueMediums = [...new Set(mockBrandingItems?.map(item => item?.brandingMedium))];
    
    uniqueMediums?.forEach(medium => {
      // For each medium, find KVs that match the same branding type/medium
      kvsByMedium[medium] = keyVisuals?.filter(kv => 
        kv?.id !== selectedKV?.id && 
        kv?.kvStatus === 'Active' &&
        kv?.brandingType === medium // Match by branding medium
      );
    });
    
    setAvailableKVsByMedium(kvsByMedium);
  };

  const getBrandName = (brandId) => {
    const brand = brands?.find(b => b?.id === brandId);
    return brand?.name || 'Unknown Brand';
  };

  const getCategoryName = (categoryId) => {
    const category = categories?.find(c => c?.id === categoryId);
    return category?.name || 'Unknown Category';
  };

  // Get filtered KVs for bulk replacement (same medium as the KV being archived)
  const getBulkReplacementOptions = () => {
    if (!selectedKV?.brandingType) return [];
    
    return keyVisuals?.filter(kv => 
      kv?.id !== selectedKV?.id && 
      kv?.kvStatus === 'Active' &&
      kv?.brandingType === selectedKV?.brandingType &&
      (!searchTerm || 
        kv?.kvId?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        kv?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        getBrandName(kv?.brandId)?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      ) &&
      (!filterBrand || kv?.brandId === filterBrand) &&
      (!filterCategory || kv?.categoryId === filterCategory)
    );
  };

  // Handle individual replacement selection
  const handleIndividualReplacement = (itemIndex, replacementKVId) => {
    const selectedKV = availableKVsByMedium?.[individualReplacements?.[itemIndex]?.brandingMedium]?.find(kv => kv?.id === replacementKVId) || null;
    
    setIndividualReplacements(prev => 
      prev?.map((item, index) => 
        index === itemIndex 
          ? { ...item, replacementKVId: replacementKVId, replacementKV: selectedKV }
          : item
      )
    );
    
    // Clear validation errors for this item
    setValidationErrors(prev => prev?.filter(error => error?.itemIndex !== itemIndex));
  };

  // Handle bulk replacement mode
  const handleBulkReplacement = (replacementKVId) => {
    const replacementKV = getBulkReplacementOptions()?.find(kv => kv?.id === replacementKVId) || null;
    setBulkReplacementKV(replacementKV);
    
    // Auto-fill all compatible individual replacements
    setIndividualReplacements(prev => 
      prev?.map(item => {
        // Only replace if the KV is compatible with this item's branding medium
        const isCompatible = replacementKV?.brandingType === item?.brandingMedium;
        return isCompatible 
          ? { ...item, replacementKVId: replacementKV?.id, replacementKV: replacementKV }
          : item; // Keep existing selection if not compatible
      })
    );
    
    // Clear validation errors
    setValidationErrors([]);
  };

  // Enhanced preview component for Old vs New KV comparison
  const KVComparisonPreview = ({ kv, type = 'old', className = '' }) => {
    if (!kv) {
      return (
        <div className="w-24 h-16 bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
          <Icon name="ImageOff" size={16} className="text-gray-400" />
        </div>
      );
    }

    return (
      <div className={`relative rounded overflow-hidden border-2 bg-gray-50 w-24 h-16 ${
        type === 'old' ? 'border-red-200' : 'border-green-200'
      } ${className}`}>
        {kv?.thumbnailUrl ? (
          <img 
            src={kv?.thumbnailUrl}
            alt={kv?.title || 'KV Preview'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Icon name="ImageOff" size={16} />
          </div>
        )}
        {kv?.fileType === 'video' && (
          <>
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="bg-white/90 rounded-full p-1">
                <Icon name="Play" size={10} className="text-gray-700" />
              </div>
            </div>
            {kv?.durationDisplay && (
              <div className="absolute bottom-0 right-0 bg-black/70 text-white text-xs px-1">
                {kv?.durationDisplay}
              </div>
            )}
          </>
        )}
        <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold text-white ${
          type === 'old' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          {type === 'old' ? '−' : '+'}
        </div>
      </div>
    );
  };

  // Validation function - CRITICAL: All dependencies must have valid replacements
  const validateReplacements = () => {
    const errors = [];
    
    individualReplacements?.forEach((item, index) => {
      if (!item?.replacementKV) {
        errors?.push({
          itemIndex: index,
          itemId: item?.id,
          message: 'Replacement KV required.',
          brandingId: item?.brandingId,
          brandingMedium: item?.brandingMedium
        });
      } else {
        // Validate that replacement KV is compatible with branding medium
        if (item?.replacementKV?.brandingType !== item?.brandingMedium) {
          errors?.push({
            itemIndex: index,
            itemId: item?.id,
            message: `Incompatible KV type. Required: ${item?.brandingMedium}, Selected: ${item?.replacementKV?.brandingType}`,
            brandingId: item?.brandingId,
            brandingMedium: item?.brandingMedium
          });
        }
      }
      
      // Check if no alternate KV exists for this branding medium
      const availableForMedium = availableKVsByMedium?.[item?.brandingMedium] || [];
      if (availableForMedium?.length === 0) {
        errors?.push({
          itemIndex: index,
          itemId: item?.id,
          message: `No available KV for this Branding Medium. Create a new KV first.`,
          brandingId: item?.brandingId,
          brandingMedium: item?.brandingMedium,
          isBlocking: true
        });
      }
    });
    
    setValidationErrors(errors);
    return errors?.length === 0;
  };

  // Handle continue to summary with validation
  const handleContinueToSummary = () => {
    if (!validateReplacements()) {
      alert('⚠ Cannot proceed. All affected Branding Items must have valid replacement KVs selected.');
      return;
    }

    // Generate replacement summary
    const summary = individualReplacements?.map(item => ({
      ...item,
      oldKVId: selectedKV?.kvId,
      oldKV: selectedKV,
      newKVId: item?.replacementKV?.kvId,
      newKVTitle: item?.replacementKV?.title
    }));

    setReplacementSummary(summary);
    setShowSummary(true);
  };

  // Handle final confirmation and archiving
  const handleConfirmReplacement = () => {
    // Create installation tracking entries with proper Branding IDs
    const installationEntries = replacementSummary?.map(item => ({
      id: `INST_${Date.now()}_${item?.id}`,
      storeName: item?.storeName,
      storeLocation: item?.location,
      storeCode: item?.storeCode,
      brandName: getBrandName(selectedKV?.brandId),
      brandingType: item?.brandingMedium, // Use proper Branding Medium term
      brandingId: item?.brandingId,
      oldKVId: item?.oldKVId,
      newKVId: item?.newKVId,
      status: 'Planned', // Default status for new entries
      installationDate: new Date()?.toISOString()?.split('T')?.[0],
      createdOn: new Date()?.toISOString(),
      assignedTo: item?.assignedTo,
      teamName: item?.assignedTo === 'Store Manager' ? 'Store Team' : 'Regional Team',
      teamLead: item?.assignedTo,
      totalCost: 0,
      quantity: 1,
      notes: `KV replacement: ${item?.oldKVId} → ${item?.newKVId}`,
      photos: [],
      timeline: [
        {
          title: 'Replacement Planned',
          description: `KV replacement scheduled: ${item?.oldKVId} → ${item?.newKVId}`,
          date: new Date()?.toISOString()?.split('T')?.[0],
          icon: 'Calendar'
        }
      ],
      replacementInfo: {
        originalKVId: item?.oldKVId,
        newKVId: item?.newKVId,
        triggerUser: 'Current User',
        triggerDate: new Date()?.toISOString(),
        replacementType: 'KV Archive Replacement'
      }
    }));

    // Call the replacement confirmation handler
    onReplacementConfirm({
      selectedKV,
      replacementKVs: individualReplacements?.map(item => item?.replacementKV)?.filter(Boolean),
      replacementSummary,
      installationEntries,
      auditInfo: {
        triggerUser: 'Current User',
        triggerDate: new Date()?.toISOString(),
        affectedBrandingItems: replacementSummary?.length,
        replacementType: replacementMode
      }
    });

    // Close modal
    onClose();
    setShowSummary(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-6xl max-h-[95vh] overflow-hidden">
        
        {!showSummary ? (
          <>
            {/* ENHANCED Header with mandatory replacement messaging */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Replace & Archive KV</h2>
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium mb-1">
                      ⚠ This KV is currently in use. Select replacement KVs for all affected Branding Items to proceed.
                    </p>
                    <p className="text-xs text-yellow-700">
                      KV ID: <span className="font-mono">{selectedKV?.kvId}</span> • 
                      Found: <span className="font-medium">{mockBrandingItems?.length} dependencies</span>
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>

            {/* ENHANCED Dependencies List with Store Code + Branding ID + Branding Medium + Previews */}
            <div className="p-6 border-b border-border bg-red-50/30">
              <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} className="text-red-600" />
                Dependency Check - Branding Items requiring replacement
              </h3>
              <div className="space-y-2">
                {mockBrandingItems?.map((item, index) => {
                  const hasError = validationErrors?.some(error => error?.itemIndex === index);
                  const itemError = validationErrors?.find(error => error?.itemIndex === index);
                  const availableKVs = availableKVsByMedium?.[item?.brandingMedium] || [];
                  
                  // Create options for the KV select
                  const kvOptions = availableKVs?.map(kv => ({
                    value: kv?.id,
                    kvId: kv?.kvId,
                    title: kv?.title,
                    brandId: kv?.brandId,
                    brandingType: kv?.brandingType,
                    thumbnailUrl: kv?.thumbnailUrl,
                    fileType: kv?.fileType,
                    durationDisplay: kv?.durationDisplay,
                    dimensions: kv?.dimensions
                  }));
                  
                  return (
                    <div key={item?.id} className={`p-4 border rounded-lg ${
                      hasError ? 'border-red-300 bg-red-50' : 'border-border bg-card'
                    }`}>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                        <div>
                          <div className="text-sm text-muted-foreground">Store Code</div>
                          <div className="font-mono text-sm font-medium text-foreground">
                            {item?.storeCode}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">Store Name</div>
                          <div className="font-medium text-foreground">{item?.storeName}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">Branding ID</div>
                          <div className="font-mono text-xs font-medium text-foreground">{item?.brandingId}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">Branding Medium</div>
                          <div className="font-medium text-foreground">{item?.brandingMedium}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground">Current KV ID</div>
                          <div className="font-mono text-xs text-red-600">{item?.currentKVId}</div>
                        </div>
                        <div>
                          <div className="text-sm text-muted-foreground mb-2">Replacement KV</div>
                          {availableKVs?.length === 0 ? (
                            <div className="text-xs text-red-600 font-medium">
                              No KVs available for {item?.brandingMedium}
                            </div>
                          ) : (
                            <KVSelectWithPreview
                              options={kvOptions}
                              value={individualReplacements?.[index]?.replacementKVId || ''}
                              onChange={(value) => handleIndividualReplacement(index, value)}
                              placeholder="Choose replacement..."
                              error={hasError}
                              getBrandName={getBrandName}
                              showSelectedPreview={true}
                              className=""
                            />
                          )}
                        </div>
                      </div>
                      {hasError && (
                        <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
                          <Icon name="AlertCircle" size={12} />
                          {itemError?.message}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ENHANCED Replacement Mode Selection with Previews */}
            <div className="p-6 border-b border-border bg-blue-50/30">
              <h3 className="font-medium text-foreground mb-3">Replacement Mode</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  replacementMode === 'bulk' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`} onClick={() => setReplacementMode('bulk')}>
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      replacementMode === 'bulk' ? 'border-primary bg-primary' : 'border-border'
                    }`}>
                      {replacementMode === 'bulk' && <Icon name="Check" size={8} className="text-white" />}
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Bulk Replace All</div>
                      <div className="text-sm text-muted-foreground">Choose one KV for all compatible items</div>
                    </div>
                  </div>
                  {replacementMode === 'bulk' && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <KVSelectWithPreview
                        options={getBulkReplacementOptions()?.map(kv => ({
                          value: kv?.id,
                          kvId: kv?.kvId,
                          title: kv?.title,
                          brandId: kv?.brandId,
                          brandingType: kv?.brandingType,
                          thumbnailUrl: kv?.thumbnailUrl,
                          fileType: kv?.fileType,
                          durationDisplay: kv?.durationDisplay,
                          dimensions: kv?.dimensions
                        }))}
                        value={bulkReplacementKV?.id || ''}
                        onChange={handleBulkReplacement}
                        placeholder="Choose bulk replacement KV"
                        getBrandName={getBrandName}
                        showSelectedPreview={true}
                        className=""
                      />
                      <div className="text-xs text-muted-foreground mt-2">
                        Only shows KVs matching the archived KV's Branding Medium: {selectedKV?.brandingType}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  replacementMode === 'individual' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`} onClick={() => setReplacementMode('individual')}>
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      replacementMode === 'individual' ? 'border-primary bg-primary' : 'border-border'
                    }`}>
                      {replacementMode === 'individual' && <Icon name="Check" size={8} className="text-white" />}
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Individual Replace</div>
                      <div className="text-sm text-muted-foreground">Set different KV per Branding Item</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ENHANCED Validation Summary */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-foreground">Replacement Status</h3>
                  <div className="mt-2">
                    {validationErrors?.length === 0 ? (
                      <div className="text-sm text-green-600 flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} />
                        All Branding Items have valid replacements
                      </div>
                    ) : (
                      <div className="text-sm text-red-600 flex items-center gap-2">
                        <Icon name="AlertTriangle" size={16} />
                        {validationErrors?.length} item(s) need replacement KVs
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Completed: {individualReplacements?.filter(item => item?.replacementKV)?.length}/{individualReplacements?.length}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  onClick={handleContinueToSummary}
                  disabled={validationErrors?.length > 0 || individualReplacements?.filter(item => item?.replacementKV)?.length === 0}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className={validationErrors?.length === 0 ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {validationErrors?.length === 0 ? 'Confirm Replace & Archive' : `Fix ${validationErrors?.length} Error(s) First`}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ENHANCED Summary View with Previews */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Replacement Confirmation</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Review and confirm KV replacements and archiving
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSummary(false)}
                >
                  <Icon name="ArrowLeft" size={16} />
                </Button>
              </div>
            </div>

            <div className="p-6 flex-1 overflow-auto max-h-96">
              {/* Enhanced KV Comparison with Previews */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                    <Icon name="Archive" size={16} />
                    KV to be Archived
                  </h3>
                  <div className="flex items-start gap-4">
                    <KVComparisonPreview kv={selectedKV} type="old" />
                    <div className="space-y-2 text-sm flex-1">
                      <div><span className="font-medium">KV ID:</span> {selectedKV?.kvId}</div>
                      <div><span className="font-medium">Title:</span> {selectedKV?.title}</div>
                      <div><span className="font-medium">Brand:</span> {getBrandName(selectedKV?.brandId)}</div>
                      <div><span className="font-medium">Branding Medium:</span> {selectedKV?.brandingType}</div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                    <Icon name="Plus" size={16} />
                    Replacement Summary
                  </h3>
                  <div className="text-sm">
                    <div><span className="font-medium">Total Replacements:</span> {replacementSummary?.length}</div>
                    <div><span className="font-medium">Replacement Mode:</span> {replacementMode === 'bulk' ? 'Bulk Replace' : 'Individual Replace'}</div>
                    <div><span className="font-medium">Unique KVs Used:</span> {[...new Set(replacementSummary?.map(item => item?.newKVId))]?.length}</div>
                  </div>
                </div>
              </div>

              {/* Enhanced Detailed Replacement List with Old → New Preview */}
              <div className="mb-6">
                <h3 className="font-medium text-foreground mb-3">
                  Installation Tracking Entries to be Created ({replacementSummary?.length})
                </h3>
                <div className="space-y-3">
                  {replacementSummary?.map((item, index) => (
                    <div key={index} className="p-4 border border-border rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-7 gap-4 text-sm items-center">
                        <div>
                          <div className="text-muted-foreground">Store Name</div>
                          <div className="font-medium text-foreground">{item?.storeName}</div>
                          <div className="text-xs text-muted-foreground">{item?.storeCode}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Branding Medium</div>
                          <div className="font-medium">{item?.brandingMedium}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Branding ID</div>
                          <div className="font-mono text-xs font-medium text-foreground">{item?.brandingId}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-muted-foreground mb-2">KV Change (Old → New)</div>
                          <div className="flex items-center gap-3">
                            <KVComparisonPreview kv={item?.oldKV} type="old" />
                            <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                            <KVComparisonPreview kv={item?.replacementKV} type="new" />
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            <span className="text-red-600">{item?.oldKVId}</span> → <span className="text-green-600">{item?.newKVId}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Status</div>
                          <div className="font-medium text-blue-600">Planned</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Assigned To</div>
                          <div className="font-medium">{item?.assignedTo}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Installation Tracking Integration Notice */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <Icon name="Calendar" size={16} />
                  Installation Tracking Integration
                </h3>
                <div className="text-sm text-blue-700 space-y-1">
                  <div>✓ Automatically create Installation Tracking entries for each updated Branding Item</div>
                  <div>✓ Status workflow: Planned → Installed → Verified</div>
                  <div>✓ All replacements will appear in Installation Tracking for follow-up</div>
                  <div>✓ Audit trail maintained for KV replacement process</div>
                </div>
              </div>
            </div>

            {/* Final Confirmation Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowSummary(false)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Replacements
              </Button>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleConfirmReplacement}
                  iconName="Check"
                  iconPosition="left"
                  className="bg-green-600 hover:bg-green-700"
                >
                  Confirm Replace & Archive
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KVReplacementModal;